import EmblaCarousel from 'embla-carousel';

/**
 * ppCarousel — the theme's only carousel.
 *
 * A thin Alpine wrapper over Embla, which replaced Slick and Swiper. Embla owns
 * the hard parts — drag with momentum, seamless looping, snap points, resize —
 * and this component only exposes them to the markup and tracks which slide is
 * showing so dots and arrows can bind to it.
 *
 * The markup needs three levels. Embla measures the viewport, translates the
 * track, and treats the track's children as the slides:
 *
 *   <div x-data="ppCarousel({ loop: true })" class="pp-carousel">
 *     <div class="pp-carousel__viewport" x-ref="viewport">
 *       <div class="pp-carousel__track">
 *         <div class="pp-carousel__slide">…</div>
 *       </div>
 *     </div>
 *     <button @click="prev()" :disabled="!canPrev">…</button>
 *     <template x-for="i in count" :key="i">
 *       <button @click="go(i - 1)" :class="{ 'is-active': selected === i - 1 }"></button>
 *     </template>
 *   </div>
 *
 * Options:
 *   loop     — seamless infinite scrolling. Off by default.
 *   align    — where a slide settles in the viewport. 'start' by default.
 *   autoplay — milliseconds between advances. 0 (default) is off; it stops for
 *              good the moment someone interacts.
 *
 * Slides per view is the stylesheet's business, not a config option — see
 * .pp-carousel--multi in assets/scss/lib/_carousel.scss.
 */
export default function registerCarousel() {

	document.addEventListener('alpine:init', () => {

		window.Alpine.data('ppCarousel', (options = {}) => ({

			embla: null,
			selected: 0,
			count: 0,
			canPrev: false,
			canNext: false,
			timer: null,

			init() {
				var viewport = this.$refs.viewport;

				if (!viewport) {
					return;
				}

				var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

				this.embla = EmblaCarousel(viewport, {
					loop: !!options.loop,
					align: options.align || 'start',
					containScroll: 'trimSnaps',
					duration: reduced ? 0 : 25
				});

				var self = this;

				var update = function () {
					self.selected = self.embla.selectedScrollSnap();
					self.count = self.embla.scrollSnapList().length;
					self.canPrev = self.embla.canScrollPrev();
					self.canNext = self.embla.canScrollNext();
				};

				this.embla.on('select', update);
				this.embla.on('reInit', update);
				update();

				if (options.autoplay) {
					this.startAutoplay(options.autoplay);
				}
			},

			destroy() {
				this.stopAutoplay();

				if (this.embla) {
					this.embla.destroy();
					this.embla = null;
				}
			},

			next() {
				if (this.embla) this.embla.scrollNext();
			},

			prev() {
				if (this.embla) this.embla.scrollPrev();
			},

			go(index) {
				if (this.embla) this.embla.scrollTo(index);
			},

			startAutoplay(ms) {
				var self = this;

				this.timer = setInterval(function () {
					if (self.embla) self.embla.scrollNext();
				}, ms);

				// Stop nagging someone who is already interacting with it.
				this.embla.on('pointerDown', function () { self.stopAutoplay(); });
				this.$el.addEventListener('focusin', function () { self.stopAutoplay(); }, { once: true });
			},

			stopAutoplay() {
				clearInterval(this.timer);
				this.timer = null;
			}
		}));
	});
}
